import type { Notification as NotificationData } from '@mathflat/domain/Notification'
import { isAfter, isBefore, isToday } from 'date-fns'
import { makeAutoObservable, runInAction } from 'mobx'
import { TOAST_STR } from 'src/constant'
import type { NotificationCTA } from 'src/dto'
import { Notification, NotificationColl } from 'src/dto'

import { notificationController } from '@/controller/notification.controller'
import ExtendedMap from '@/models/ExtendedMap'
import { localStorageService } from '@/service/storage.service'
import { toastDelete } from '@/utils/toast'

import { releaseNotificationUtils } from './notificationUtils'

class NotificationService {
  isOpen = false
  unreadCount = -1
  newNotiCountSinceOpen = 0
  private _serverNotifications?: NotificationColl<Notification>
  private _hasBefore = true
  publicWorkbookSupportedNotification: Notification | null = null
  essayWorksheetNotification: Notification | null = null
  constructor() {
    makeAutoObservable(this)
  }

  private get _allNotifications() {
    // client관리 알림과 서버관리 알림을 병합
    const clientNotifications = [
      this.essayWorksheetNotification,
      this.publicWorkbookSupportedNotification,
    ].filter((n): n is Notification => !!n)

    if (clientNotifications.length === 0) return this._serverNotifications

    if (this._serverNotifications?.size === 0 && !this._hasBefore) {
      return new NotificationColl(ExtendedMap.fromArr(clientNotifications, 'sortKey'))
    }

    const serverNotifications = this._serverNotifications?.toArr ?? []

    const newestClient = clientNotifications.reduce((acc, cur) =>
      acc.sortKey > cur.sortKey ? acc : cur,
    )
    const isWithinServerNotificationRange = this._isWithinServerNotificationRange(
      newestClient,
      serverNotifications,
    )

    if (isWithinServerNotificationRange || !this._hasBefore) {
      const combinedNotifications = new NotificationColl(
        ExtendedMap.fromArr(serverNotifications, 'sortKey'),
      )
      combinedNotifications.addsByArr(clientNotifications)
      return combinedNotifications
    }

    const newestServerSortKey = Math.max(...serverNotifications.map((n) => n.sortKey))
    if (newestClient.sortKey > newestServerSortKey) {
      const combinedNotifications = new NotificationColl(
        ExtendedMap.fromArr(serverNotifications, 'sortKey'),
      )
      combinedNotifications.addsByArr(clientNotifications)
      return combinedNotifications
    }

    return this._serverNotifications
  }

  get isEmptyUnexpiredNotifications() {
    return this.unExpiredAndWithoutStartNotifications.length === 0
  }

  get unExpiredAndWithoutStartNotifications() {
    return (
      this._allNotifications?.toArr
        .filter(({ expiredAt, state }) => isBefore(new Date(), expiredAt) && state !== 'START')
        .sort((a, b) => b.sortKey - a.sortKey) ?? []
    )
  }
  get todayNotifications() {
    return this.unExpiredAndWithoutStartNotifications.filter(({ createdAt }) => isToday(createdAt))
  }
  get pastNotifications() {
    return this.unExpiredAndWithoutStartNotifications.filter(({ createdAt }) => !isToday(createdAt))
  }
  get unreadCountWithLimit() {
    if (this.unreadCount <= 0) return ''
    if (this.unreadCount > 99) return '99+'
    return this.unreadCount
  }

  open = () => {
    this.newNotiCountSinceOpen = 0
    this.isOpen = true
  }
  close = () => {
    this.isOpen = false
  }

  clearNewNotiCountSinceOpen = () => {
    this.newNotiCountSinceOpen = 0
  }

  handleItemClick = (sortKey: Notification['sortKey']) => {
    const notification = this._allNotifications!.findBySortKey(sortKey)

    if (notification) {
      this._read(notification)
      this.checkIsExpired(notification)
    }
  }

  handleCTAClick = ({
    sortKey,
    action,
  }: Pick<Notification, 'sortKey'> & { action: NotificationCTA }) => {
    const notification = this._allNotifications?.findBySortKey(sortKey)

    if (notification) {
      this.deleteCTA({ sortKey, action })
      this._read(notification)
    }
  }

  private handleReleaseNotificationItemClick = (
    sortKey: Notification['sortKey'],
    readFn: () => Promise<void>,
  ) => {
    const notification = this._allNotifications?.findBySortKey(sortKey)
    if (notification && !notification.isRead) {
      notification.isRead = true
      if (this.unreadCount > 0) {
        this.unreadCount -= 1
      }
      readFn()
    }
  }

  handlePublicWorkbookNotificationItemClick = (sortKey: Notification['sortKey']) => {
    this.handleReleaseNotificationItemClick(sortKey, this._readPublicWorkbookNotification)
  }

  handleEssayNotificationItemClick = (sortKey: Notification['sortKey']) => {
    this.handleReleaseNotificationItemClick(sortKey, this._readEssayNotification)
  }

  checkIsExpired(notification: Notification) {
    const isExpired = isAfter(new Date(), new Date(notification.expiredAt))

    if (isExpired) {
      toastDelete(TOAST_STR.expiredNotification)
      this._serverNotifications?.deleteBySortKey(notification.sortKey)
    }
  }

  private _read = (notification: Notification) => {
    if (notification && !notification.isRead) {
      notification.readAndFetch()
      this.unreadCount -= 1
    }
  }

  private _readPublicWorkbookNotification = async () => {
    this.publicWorkbookSupportedNotification?.read()
    await releaseNotificationUtils.readNotification('PUBLIC_WORKBOOK_OPEN_NOTIFICATION')
  }
  private _readEssayNotification = async () => {
    this.essayWorksheetNotification?.read()
    await releaseNotificationUtils.readNotification('ESSAY_WORKSHEET_OPEN_NOTIFICATION')
  }
  readAll = () => {
    // 로드된 페이지가 전부 읽음이어도 서버 전체 미읽음(unreadCount)이 남아 있으면 읽음 요청을 보낸다
    const isLoadedPageAllRead = this._allNotifications?.toArr.every(({ isRead }) => isRead)
    if (isLoadedPageAllRead && this.unreadCount <= 0) return

    const partitionKey = this.partitionKey
    notificationController.readItem({ partitionKey })
    if (this.publicWorkbookSupportedNotification?.isRead === false) {
      this._readPublicWorkbookNotification()
    }
    if (this.essayWorksheetNotification?.isRead === false) {
      this._readEssayNotification()
    }
    this._serverNotifications?.readAll()
    this.unreadCount = 0
  }
  receiveNotification = (notification: NotificationData) => {
    if (this.isOpen) {
      this.newNotiCountSinceOpen += 1
    }
    this._serverNotifications?.addsByArr([new Notification(notification)])

    if (notification.data?.payload?.state !== 'START') {
      this.unreadCount += 1
    }
  }

  async fetchNotifications() {
    const partitionKey = this.partitionKey
    const { hasBefore, notificationColl } = await notificationController.fetchItems({
      partitionKey,
      params: { size: 30, searchType: 'BEFORE' },
    })

    runInAction(() => {
      this._hasBefore = hasBefore
      this._serverNotifications = notificationColl
    })
  }

  async fetchMoreNotifications() {
    if (!this._hasBefore || !this._serverNotifications) return

    const partitionKey = this.partitionKey
    const { hasBefore, notificationColl } = await notificationController.fetchItems({
      partitionKey,
      params: {
        size: 10,
        searchType: 'BEFORE',
        sortKey: this._serverNotifications?.lastNotification?.sortKey,
      },
    })

    this._hasBefore = hasBefore
    this._serverNotifications?.adds(notificationColl)
  }

  async fetchUnreadCount() {
    const partitionKey = this.partitionKey

    this.unreadCount = await notificationController.fetchUnreadCount({
      partitionKey,
    })
    const essayUnread = Number(
      releaseNotificationUtils.getIsUnRead('ESSAY_WORKSHEET_OPEN_NOTIFICATION'),
    )
    const publicUnread = Number(
      releaseNotificationUtils.getIsUnRead('PUBLIC_WORKBOOK_OPEN_NOTIFICATION'),
    )
    this.unreadCount += essayUnread + publicUnread
  }
  async deleteCTA({
    sortKey,
    action,
  }: Pick<Notification, 'sortKey'> & { action: NotificationCTA }) {
    const partitionKey = this.partitionKey
    const notification = this._allNotifications?.findBySortKey(sortKey)

    if (notification) {
      notification.ctas = notification.ctas.filter(({ action: _action }) => _action !== action)
    }

    notificationController.deleteCTA({ partitionKey, sortKey, action })
  }

  private get partitionKey() {
    const academyId = localStorageService.getItem('ACADEMY_ID')!
    const teacherId = localStorageService.getItem('teacher-id')!
    return `${academyId}.${teacherId}`
  }

  private _isWithinServerNotificationRange(
    notification: Notification,
    serverNotifications: Notification[],
  ) {
    if (serverNotifications.length === 0) return true

    const oldestServerSortKey = Math.min(...serverNotifications.map((n) => n.sortKey))
    const newestServerSortKey = Math.max(...serverNotifications.map((n) => n.sortKey))

    return (
      notification.sortKey <= newestServerSortKey && notification.sortKey >= oldestServerSortKey
    )
  }
}

export default NotificationService
